// GroupPhotoUploadModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Upload, Button, message, List } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { uploadGroupPhotos } from '../../api/group';

const GroupPhotoUploadModal = ({ visible, onClose, group }) => {
  const [filesByMember, setFilesByMember] = useState({});
  const [uploading, setUploading] = useState(false);

  const members = group?.group || [];

  useEffect(() => {
    if (visible && members.length) {
      // Reset when modal opens
      const initial = {};
      members.forEach((m) => {
        initial[m.id_number] = null;
      });
      setFilesByMember(initial);
    }
  }, [visible, members]);

  const beforeUpload = (file) => {
    const isJpgOrPng =
      file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG files!');
      return Upload.LIST_IGNORE;
    }
    return false; // prevent auto upload
  };

  const handleMemberFileChange = (idNumber) => ({ fileList }) => {
    setFilesByMember((prev) => ({
      ...prev,
      [idNumber]: fileList[0] || null,
    }));
  };

  const handleUpload = async () => {
    if (!members.length) {
      message.error('No group members found to match photos to.');
      return;
    }

    // Ensure each member has a file
    const missingMembers = members.filter(
      (m) => !filesByMember[m.id_number]?.originFileObj
    );

    if (missingMembers.length) {
      const names = missingMembers.map((m) => m.name).join(', ');
      message.error(
        `Please upload a photo for each member. Missing: ${names}`
      );
      return;
    }

    setUploading(true);
    try {
      const memberFiles = {};
      members.forEach((m) => {
        memberFiles[m.id_number] =
          filesByMember[m.id_number].originFileObj;
      });

      await uploadGroupPhotos(group._id, memberFiles);
      message.success('Photos uploaded!');
      onClose();
    } catch (e) {
      message.error(e.message || 'Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title={`Upload Group Photos${
        group?.group_name ? ` - ${group.group_name}` : ''
      }`}
      destroyOnClose
    >
      <p style={{ marginBottom: 12 }}>
        Upload one photo per member. Each upload below is tied to that
        specific member.
      </p>

      <List
        dataSource={members}
        bordered
        renderItem={(member) => (
          <List.Item
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div>
                <strong>{member.name}</strong>
              </div>
              <div style={{ fontSize: 12, color: '#888' }}>
                ID: {member.id_number}
              </div>
            </div>
            <div style={{ maxWidth: 260 }}>
              <Upload
                beforeUpload={beforeUpload}
                fileList={
                  filesByMember[member.id_number]
                    ? [filesByMember[member.id_number]]
                    : []
                }
                onChange={handleMemberFileChange(member.id_number)}
                multiple={false}
                accept=".jpg,.jpeg,.png"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select Photo</Button>
              </Upload>
            </div>
          </List.Item>
        )}
      />

      <div style={{ textAlign: 'right', marginTop: 24 }}>
        <Button
          type="primary"
          onClick={handleUpload}
          loading={uploading}
          disabled={!members.length}
          style={{ marginRight: 8 }}
        >
          Upload All
        </Button>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

export default GroupPhotoUploadModal;
